import { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useChannelBySlug } from '@/hooks/useChannels';
import { useCohortBySlug } from '@/hooks/useCohorts';
import { PostWithProfile, useChannelPostsPaged, useCohortPostsPaged } from '@/hooks/useChannelPosts';
import { useIsModerator } from '@/hooks/useUserRole';
import { PostCard } from '@/components/feed/PostCard';
import { PostComposer } from '@/components/feed/PostComposer';
import { Card, CardContent } from '@/components/ui/card';
import { Hash, Lock, Loader2, ArrowLeft, MapPin } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useBlocks } from '@/hooks/useBlocks';
import { Button } from '@/components/ui/button';
import { MemberAvatarBar } from '@/components/feed/MemberAvatarBar';

export default function ChannelFeedPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: channel, isLoading: channelLoading } = useChannelBySlug(slug || '');
  const { data: cohort, isLoading: cohortLoading } = useCohortBySlug(slug || '');

  // Use channel if found, otherwise use cohort
  const isCohortView = !channel && !!cohort;
  const isLoading = channelLoading || (channelLoading === false && !channel && cohortLoading);

  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: channelPostsPage, isLoading: channelPostsLoading } = useChannelPostsPaged({
    channelId: channel?.id,
    page,
    pageSize,
  });
  const { data: cohortPostsPage, isLoading: cohortPostsLoading } = useCohortPostsPaged({
    cohortTag: isCohortView ? (cohort?.city || cohort?.name) : undefined,
    page,
    pageSize,
  });

  const [aggregatedPosts, setAggregatedPosts] = useState<PostWithProfile[]>([]);
  const allPosts = isCohortView ? cohortPostsPage?.data : channelPostsPage?.data;
  const postsLoading = isCohortView ? cohortPostsLoading : channelPostsLoading;
  const totalCount = isCohortView ? (cohortPostsPage?.count ?? 0) : (channelPostsPage?.count ?? 0);

  useEffect(() => {
    if (!allPosts) return;
    if (page === 0) {
      setAggregatedPosts(allPosts);
      return;
    }
    setAggregatedPosts((current) => {
      const existingIds = new Set(current.map((p) => p.id));
      const next = allPosts.filter((p) => !existingIds.has(p.id));
      return [...current, ...next];
    });
  }, [allPosts, page]);

  useEffect(() => {
    setPage(0);
    setAggregatedPosts([]);
  }, [channel?.id, cohort?.id, isCohortView]);

  const { data: blocks } = useBlocks();
  const { isModerator } = useIsModerator();

  // Memoize filtered posts to prevent unnecessary re-renders
  const posts = useMemo(() => {
    if (!aggregatedPosts) return [];
    const blockedIds = new Set(blocks?.map((b) => b.blocked_id) || []);
    return aggregatedPosts.filter((p) => !blockedIds.has(p.user_id));
  }, [aggregatedPosts, blocks]);

  const canLoadMore = posts.length < totalCount;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!channel && !cohort) {
    return <Navigate to="/channels" replace />;
  }

  // Build display info
  const displayName = channel?.name || channel?.slug || cohort?.name || slug;
  const displayDescription = channel?.description || cohort?.description || '';
  const isRestricted = channel?.is_restricted || false;

  const canPost = !isRestricted || isModerator;
  const cohortTag = isCohortView ? (cohort?.city || cohort?.name || '') : 'global';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <NavLink
          to="/channels"
          className="mt-1 p-2 -ml-2 rounded-lg hover:bg-muted transition-colors lg:hidden"
        >
          <ArrowLeft className="h-5 w-5" />
        </NavLink>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {isCohortView ? (
              <MapPin className="h-6 w-6 text-primary" />
            ) : (
              <Hash className="h-6 w-6 text-primary" />
            )}
            <h1 className="text-2xl font-display font-bold text-foreground">
              {displayName}
            </h1>
            {isRestricted && (
              <Lock className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          {displayDescription && (
            <p className="text-muted-foreground text-sm mt-1">
              {displayDescription}
            </p>
          )}
        </div>
      </div>

      <MemberAvatarBar cityCohort={isCohortView ? (cohort?.city || cohort?.name) : undefined} />

      {/* Post Composer */}
      {canPost ? (
        <PostComposer cohortTag={cohortTag} channelId={channel?.id} />
      ) : (
        <Card className="card-qbn">
          <CardContent className="p-4 text-center text-muted-foreground">
            <Lock className="h-5 w-5 mx-auto mb-2" />
            <p className="text-sm">
              Only moderators and admins can post in this channel.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Posts */}
      {postsLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : posts && posts.length > 0 ? (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}
          {canLoadMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          {isCohortView ? (
            <MapPin className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          ) : (
            <Hash className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          )}
          <p className="text-muted-foreground">
            No posts yet. Be the first to share!
          </p>
        </div>
      )}
    </div>
  );
}
